import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errorText, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthLayout } from "@/components/brand/AuthLayout";
import { PilotTermsLink } from "@/components/brand/PilotTermsLink";
import { Field, FormError } from "@/components/brand/Field";

export default function SignUp() {
  const { setUser, config } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", plan: params.get("plan") === "core" ? "core" : "free" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const plans = config?.plans;

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 10) return setError("Use at least 10 characters for your password.");
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      nav("/app", { replace: true });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle="We observe and surface. You always approve before anything real happens."
      footer={<>Already have an account? <Link className="ts-link" to="/signin" data-testid="go-signin-link">Sign in</Link></>}
    >
      <form onSubmit={submit} noValidate>
        <FormError message={error} testId="signup-error" />
        {plans && (
          <fieldset className="mb-5">
            <legend className="ts-label">Plan</legend>
            <div className="grid grid-cols-2 gap-2" role="radiogroup">
              {["free", "core"].map((k) => {
                const p = plans[k];
                const active = form.plan === k;
                return (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    data-testid={`plan-option-${k}`}
                    onClick={() => setForm({ ...form, plan: k })}
                    className="text-left rounded-md border p-3 transition-colors"
                    style={{ borderColor: active ? "var(--blue)" : "var(--line)", background: active ? "var(--blue-soft)" : "#fff" }}
                  >
                    <div className="text-sm font-semibold" style={{ color: "var(--ink)" }}>{p.name}</div>
                    <div className="num text-xs mt-0.5" style={{ color: "var(--muted-text)" }}>
                      {p.price_cents === 0 ? "$0" : `${money(p.price_cents, p.currency)}/mo`}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-xs mt-2" style={{ color: "var(--muted-text)" }}>
              {form.plan === "core" ? "Payment is collected after onboarding. You start on Free Audit until it succeeds." : "No credit card. One-time snapshot of your last 3 months."}
            </p>
          </fieldset>
        )}
        <Field id="full_name" label="Your name">
          <input id="full_name" data-testid="signup-name-input" className="ts-input" autoComplete="name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
        </Field>
        <Field id="email" label="Work email">
          <input id="email" data-testid="signup-email-input" className="ts-input" type="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field id="password" label="Password" hint="At least 10 characters.">
          <input id="password" data-testid="signup-password-input" className="ts-input" type="password" autoComplete="new-password" minLength={10} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </Field>
        <button data-testid="signup-submit-button" className="ts-btn ts-btn-primary w-full mt-2" disabled={busy}>
          {busy ? "Creating…" : "Create your account"}
        </button>
        <p className="text-xs mt-4 text-center" style={{ color: "var(--muted-text)" }}>By creating an account you agree to the <PilotTermsLink />.</p>
      </form>
    </AuthLayout>
  );
}
